/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {AgGridType, RowModelTypes} from './IonGrid';
import {FilterModel} from './IonGridUtils';

// defines the Props definition for useLoadGridData...
export interface LoadGridData {
  loadData: Function;
  gridApi: AgGridType;
  filterModel: FilterModel;
  gridMode?: RowModelTypes;
};

/**
 * The hook is responsible for loading the data post of setting the filter mode
 * or any other grid-related changes and avoiding the redundant calls due to
 * the grid events while doing some settings.
 *
 * ```typescript
 *
 *  import {useLoadGridData} from '@ion/dls/src/grid/';
 *
 *  const getAssetData = useLoadGridData({
 *    loadData : getAssets,
 *    filterModel,
 *    gridApi
 *  });
 *
 * ```
 */
export const useLoadGridData = ({
  loadData,
  gridApi,
  filterModel,
  gridMode
}: LoadGridData) => {
  const initialLoad      = useRef(null);
  const hasAppliedFilter = !!(filterModel && Object.keys(filterModel).length);

  // hook will fire when the grid loaded initially...
  useEffect(() => {
    if (gridApi && hasAppliedFilter) {
      gridApi.setFilterModel(filterModel);
    }
  }, [gridApi]);

  // invoked when the filter/sort/size/refresh related event fires from grid...
  const fetchData = (params) => {
    if (!hasAppliedFilter || initialLoad.current || gridMode === RowModelTypes.ClientSide) {
      loadData(params);
    }
    initialLoad.current = true;
  };

  return fetchData;
};

export default useLoadGridData;
